<template>
    <vx-modal
        v-model:visible="visible"
        :preventClosing="props.hasTakenEffect"
        data-test="terms-owner-modal"
        padding="standard"
        size="lg"
    >
        <template v-slot:header>{{ props.pendingTerm.title }}</template>

        <vx-alert data-test="terms-pending-owner-alert" class="mb-10" color="info" :closeable="false" :shadow="true">
            <div class="font-extrabold text-base mb-1">Action required</div>
            <div v-if="props.hasTakenEffect">
                New Terms &amp; Conditions are now in effect. You as the {{ ownerOf }} Owner must accept these terms to
                restore service.
            </div>
            <div v-else>
                New Terms &amp; Conditions take effect on <strong>{{ props.formattedEffectiveAt }}</strong
                >. You as the {{ ownerOf }} Owner must accept these terms to avoid interruptions in service.
            </div>
        </vx-alert>

        <markdown-renderer :source="props.pendingTerm.content" />
        <div class="mt-6 bg-white rounded p-4 sticky left-0 bottom-0 shadow text-slate-800">
            <vx-checkbox v-model="consentChecked" data-test="terms-consent-checkbox">
                {{ props.pendingTerm.consent_text }}
            </vx-checkbox>
        </div>
        <template v-slot:footer>
            <div class="flex justify-end gap-2">
                <vx-button
                    v-if="!props.hasTakenEffect"
                    data-test="terms-cancel-button"
                    size="lg"
                    color="muted"
                    @click="visible = false"
                    >Cancel
                    <font-awesome-icon :icon="faTimesCircle"></font-awesome-icon>
                </vx-button>
                <vx-button v-if="props.hasTakenEffect" color="muted" size="lg" href="mailto:support@voxie.com">
                    Contact Support <font-awesome-icon :icon="faEnvelope"></font-awesome-icon>
                </vx-button>
                <vx-popover arrow placement="top" hover class="grow">
                    <vx-button
                        block
                        color="primary"
                        :disabled="!consentChecked"
                        :loading="submitting"
                        size="lg"
                        data-test="terms-submit-button"
                        @click="submit"
                    >
                        {{ props.pendingTerm.consent_button }}
                        <font-awesome-icon :icon="faCheckCircle"></font-awesome-icon>
                    </vx-button>
                    <template v-if="!consentChecked" v-slot:content
                        >You must check "{{ props.pendingTerm.consent_text }}" before proceeding</template
                    >
                </vx-popover>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { VxAlert, VxButton, VxCheckbox, VxModal, VxPopover } from '@voxie/frontend-components';
import MarkdownRenderer from '~/components/general/MarkdownRenderer.vue';
import { useToasted } from '~/composables/useToasted';
import type { Term } from '~/services/termsClient.types';
import { catchRequestError } from '~/utils/request';
import {
    acceptActiveTerm,
    currentTermsScope,
    isTermsScopeBillable,
    redirectToBillingAfterTermsAccepted,
} from '~/utils/termsScope';
import { faCheckCircle, faEnvelope, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const toasted = useToasted();

const teamId = Spark.state.currentTeam?.id ?? null;
const scope = currentTermsScope();
const ownerOf = scope?.orgUnitId ? 'OU' : 'group';

const visible = defineModel<boolean>('visible');

const props = defineProps<{
    pendingTerm: Term;
    hasTakenEffect: boolean;
    formattedEffectiveAt: string;
}>();

const emit = defineEmits(['accepted']);

const consentChecked = ref(false);
const submitting = ref(false);

const submit = async () => {
    if (!props.pendingTerm || !teamId || !scope || !consentChecked.value) {
        return;
    }

    submitting.value = true;

    try {
        await acceptActiveTerm(teamId, scope, props.pendingTerm.id);
        visible.value = false;
        consentChecked.value = false;
        toasted.success('Terms & Conditions accepted successfully');
        if (isTermsScopeBillable(scope)) {
            redirectToBillingAfterTermsAccepted(scope);
        }
        emit('accepted');
    } catch (error: unknown) {
        toasted.error(catchRequestError(error));
    } finally {
        submitting.value = false;
    }
};
</script>
